import { Box, Drawer, Stack, Theme } from '@mui/material';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { createUseStyles } from 'react-jss';
import ShortcutTab from './shortcut-tab';
import HistoryTab from './history-tab';
import useApp from '@/hooks/use-app.hook';
import UserMenu from '../user-menu';
import UserSettingsTab from './user-settings-tab';
import AgentTab from './agent-tab';
import { isChatPath, TAB_ID } from './helpers';
import HomeLinkBarSection from './link-bar-section/home';
import ChatLinkBarSection from './link-bar-section/chat';
import LinkBarItem from './link-bar-item';
import {
  getSidebarBarsOpenState,
  setSidebarBarsOpenState,
} from '@/lib/services/local-settings.service';
import { LoadState } from '@/lib/helpers';
import useFeature from '@/hooks/use-feature.hook';

const LINK_BAR_WIDTH = 74;
const LINK_BAR_WIDTH_OPEN = LINK_BAR_WIDTH + 80;
const TAB_BAR_WIDTH = 292;
const SLIDE_OUT_TRANSITION = 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)';

const getSidebarWith = (tabOpen: boolean, linkBarOpen: boolean) => {
  let width = LINK_BAR_WIDTH;

  if (linkBarOpen) {
    width = LINK_BAR_WIDTH_OPEN;
  }

  if (tabOpen) {
    width += TAB_BAR_WIDTH;
  }

  return width;
};

const useStyles = createUseStyles<string, { tabOpen: boolean; linkBarOpen: boolean }>(
  (theme: Theme) => ({
    sidebar: ({ tabOpen, linkBarOpen }) => ({
      width: getSidebarWith(tabOpen, linkBarOpen),
      height: '100%',
      position: 'relative',
      transition: SLIDE_OUT_TRANSITION,
    }),
    drawer: ({ tabOpen, linkBarOpen }) => ({
      width: getSidebarWith(tabOpen, linkBarOpen),
      height: '100%',
      transition: SLIDE_OUT_TRANSITION,
      backgroundColor: theme.palette.common.white,
      overflow: 'hidden',
      '& > .MuiPaper-root': {
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        border: 'none',
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '& .sidebar-button': {
        height: 50,
      },
    }),
    linkBar: ({ linkBarOpen }) => ({
      width: linkBarOpen ? LINK_BAR_WIDTH_OPEN : LINK_BAR_WIDTH,
      height: '100%',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      overflow: 'hidden',
      boxSizing: 'border-box',
      backgroundColor: theme.palette.background.linkbar,
      color: theme.palette.text.primary,
      transition: SLIDE_OUT_TRANSITION,
    }),
    tabBar: ({ tabOpen }) => ({
      width: tabOpen ? TAB_BAR_WIDTH : 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      height: '100%',
      overflow: 'hidden',
      transition: SLIDE_OUT_TRANSITION,
      boxSizing: 'border-box',
      backgroundColor: theme.palette.background.sidebar,
    }),
    tabBarInner: ({ tabOpen }) => ({
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      height: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      padding: '11px 0px 0px 0px',
      opacity: tabOpen ? 1 : 0,
      transition: theme.transitions.create('opacity', {
        delay: 200,
        easing: theme.transitions.easing.sharp,
        duration: 10,
      }),
    }),
    logo: {
      position: 'absolute',
      top: 20,
      left: LINK_BAR_WIDTH + 20,
      zIndex: theme.zIndex.drawer + 10,
      width: 'auto',
      overflow: 'hidden',
    },
  })
);

const AppSidebar: FC = () => {
  const { signedIn } = useApp();
  const { canUseChat } = useFeature();
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const isChat = useMemo(() => isChatPath(pathname), [pathname]);

  const [activeTab, setActiveTab] = useState<TAB_ID>('none');
  const [sectionOpen, setSectionOpen] = useState(
    canUseChat && isChat ? [false, true] : [true, false]
  ); // [home, chat]
  const [linkBarOpen, setLinkBarOpen] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  const tabOpen = activeTab !== 'none';

  const styles = useStyles({
    tabOpen,
    linkBarOpen,
  });

  const handleSetActiveTab = useCallback(
    (tabId: TAB_ID) => {
      setActiveTab(tabId);
      setSidebarBarsOpenState({ activeTab: isChat ? tabId : 'none', linkBarOpen });
    },
    [isChat, linkBarOpen]
  );

  const handleToggleLinkBar = () => {
    const updated = !linkBarOpen;
    setLinkBarOpen(updated);
    setSidebarBarsOpenState({ activeTab, linkBarOpen: updated });
  };

  const handleCloseTabBar = () => {
    setActiveTab('none');
  };

  const handleClickSection = (index: number) => {
    const updated = [false, false];
    updated[index] = true;
    setSectionOpen(updated);

    // home section open? close tabs since home has none
    if (index === 0) {
      handleCloseTabBar();
    }
  };

  const handleClickTab = (tabId: TAB_ID) => {
    if (activeTab === tabId) {
      handleCloseTabBar();
      return;
    }

    handleSetActiveTab(tabId);
  };

  useEffect(() => {
    if (isChatPath(pathname)) {
      setSectionOpen([false, true]);
    }
  }, [pathname]);

  useLayoutEffect(() => {
    if (!signedIn) {
      setLoadState('loaded');
      return;
    }
    const localSettings = getSidebarBarsOpenState();

    if (!localSettings) {
      setLoadState('loaded');
      return;
    }

    const { activeTab, linkBarOpen } = localSettings;

    setActiveTab(activeTab && isChat && canUseChat ? activeTab : 'none');

    setLinkBarOpen(!!linkBarOpen);
    setLoadState('loaded');
  }, [signedIn, isChat, canUseChat]);

  if (loadState !== 'loaded') {
    return null;
  }

  return (
    <Box className={styles.sidebar}>
      <Drawer open={true} className={styles.drawer} variant="permanent" anchor="left">
        <Stack className={styles.linkBar} width="100%" alignItems="center" justifyContent="center">
          <LinkBarItem
            icon={linkBarOpen ? 'sidebar-collapse' : 'sidebar-expand'}
            onClick={handleToggleLinkBar}
            hideLabel={!linkBarOpen}
            className="sidebar-button"
          />

          <HomeLinkBarSection
            onClick={() => handleClickSection(0)}
            open={sectionOpen[0]}
            linkBarOpen={linkBarOpen}
          />
          {canUseChat && (
            <ChatLinkBarSection
              onClickTab={handleClickTab}
              activeTab={activeTab}
              onClick={() => handleClickSection(1)}
              open={sectionOpen[1]}
              linkBarOpen={linkBarOpen}
            />
          )}
          <Stack flexGrow={1} justifyContent="flex-end" pb={2}>
            {signedIn && <UserMenu />}
          </Stack>
        </Stack>

        <Box className={styles.tabBar}>
          <Box className={styles.tabBarInner}>
            {activeTab === 'agents' && <AgentTab onClose={handleCloseTabBar} />}
            {activeTab === 'shortcuts' && <ShortcutTab onClose={handleCloseTabBar} />}
            {activeTab === 'history' && <HistoryTab onClose={handleCloseTabBar} />}
            {activeTab === 'user-settings' && <UserSettingsTab onClose={handleCloseTabBar} />}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AppSidebar;
